/**
 * Configuration validation utilities
 * Validates OcChatConfig before widget initialization
 */

import { OcChatConfig } from '../../../types/oc';

/**
 * Validation error with field information
 */
export class ConfigValidationError extends Error {
  constructor(
    message: string,
    public field?: string,
  ) {
    super(message);
    this.name = 'ConfigValidationError';
  }
}

/**
 * Validates GUID format (with or without hyphens)
 */
const isValidGuid = (value: string): boolean => {
  const guidRegex =
    /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;
  return guidRegex.test(value);
};

/**
 * Validates required string field
 */
const validateRequiredString = (
  value: unknown,
  fieldName: string,
): void => {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new ConfigValidationError(
      `${fieldName} is required and must be a non-empty string`,
      fieldName,
    );
  }
};

/**
 * Validates OcChatConfig object
 * Throws ConfigValidationError if validation fails
 */
export const validateOcChatConfig = (config: unknown): void => {
  // Check config object exists
  if (!config || typeof config !== 'object') {
    throw new ConfigValidationError('Configuration object is required');
  }

  const cfg = config as Record<string, unknown>;

  // Validate required fields
  validateRequiredString(cfg.aiAssistantKey, 'aiAssistantKey');
  validateRequiredString(cfg.tenantId, 'tenantId');
  validateRequiredString(cfg.loginUrl, 'loginUrl');

  // Note: Authentication is validated at runtime via API calls, not at config validation time.
  // If iamKey is not provided, the widget will attempt to use cookie-based authentication
  // (HLTenantKey and HLAuthToken headers) when making API calls. The widget can load
  // even if the user is not authenticated - authentication state is determined by API responses.

  // Validate aiAssistantKey is a valid GUID
  if (!isValidGuid(cfg.aiAssistantKey as string)) {
    throw new ConfigValidationError(
      'aiAssistantKey must be a valid GUID format',
      'aiAssistantKey',
    );
  }
};

/**
 * Type guard to check if config is valid
 * Returns true if valid, false otherwise
 */
export const isValidOcChatConfig = (config: unknown): config is OcChatConfig => {
  try {
    validateOcChatConfig(config);
    return true;
  } catch {
    return false;
  }
};

